import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonThreading = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Threading & The GIL</h1>
                <p className="subtitle">Why Python threads can't calculate Pi faster, but can download internet data at lightspeed.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine a <strong>Restaurant Kitchen</strong> (Your CPU) with only <strong>One Stove</strong> (The GIL).
                    </p>
                    <ul>
                        <li><strong>Concurrency (Threading):</strong> You have 4 chefs. They can all chop vegetables, wash dishes, and take orders <em>at the same time</em>. But when it comes to <em>cooking</em> (executing CPU bytecode), they must take turns using the single Stove.</li>
                        <li><strong>The Result:</strong> If the work is "chopping/waiting" (I/O Bound), 4 chefs are faster. If the work is "cooking" (CPU Bound), 4 chefs are just as slow as 1, plus they get in each other's way.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The GIL (Global Interpreter Lock)</h2>
                <p>
                    The GIL is Python's most famous "feature". It is a lock that prevents multiple native threads from executing Python bytecodes at once.
                    This prevents memory management (reference counting) issues but limits pure CPU parallelism.
                </p>
                <CodeBlock language="python" code={`import threading
import time

def cpu_task():
    # Attempting to use 2 threads for math will NOT be faster
    # They fight over the GIL.
    for _ in range(10**7):
        pass

def io_task():
    # Waiting for network/disk RELEASES the GIL!
    # Other threads can run while this sleeps.
    time.sleep(2) 
    print("Download complete")

# This works great!
t1 = threading.Thread(target=io_task)
t2 = threading.Thread(target=io_task)
t1.start()
t2.start()`} />
            </div>

            <div className="content-section">
                <h2>3. The Danger: Race Conditions</h2>
                <p>
                    Threads share memory. If two threads modify the same variable, they can overwrite each other's work. This is called a <strong>Race Condition</strong>.
                </p>
                <CodeBlock language="python" code={`import threading

balance = 0
lock = threading.Lock()

def deposit():
    global balance
    for _ in range(100000):
        #  UNSAFE: balance += 1 is not atomic!
        # It involves: Read, Add, Write. Context switch usually happens in between.
        
        # SAFE: Use a Lock
        with lock:
            balance += 1

t1 = threading.Thread(target=deposit)
t2 = threading.Thread(target=deposit)

t1.start()
t2.start()
t1.join()
t2.join()

print(balance) # With lock: 200000. Without lock: Random number (e.g., 145921)`} />
            </div>

            <div className="content-section">
                <h2>4. Modern Pattern: ThreadPoolExecutor</h2>
                <p>
                    Manually creating `Thread` objects is the "old way". It's hard to manage return values and exceptions.
                    The modern standard is `concurrent.futures`.
                </p>
                <CodeBlock language="python" code={`from concurrent.futures import ThreadPoolExecutor
import time
import { ProTip } from '../../components/LessonUI';

def fetch_url(url):
    time.sleep(1) # Simulate request
    return f"Data from {url}"

urls = ["google.com", "bing.com", "yahoo.com"]

# Automatically manages a pool of 3 worker threads
with ThreadPoolExecutor(max_workers=3) as executor:
    results = executor.map(fetch_url, urls)

for res in results:
    print(res)`} />
            </div>

            <div className="content-section">
                <h2>5. When to use Threading?</h2>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Scenario</th>
                            <th>Use Threading?</th>
                            <th>Why?</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Web Scraping (100 sites)</td>
                            <td>YES</td>
                            <td>Waiting for network releases GIL. Huge speedup.</td>
                        </tr>
                        <tr>
                            <td>Video Processing / Math</td>
                            <td> NO</td>
                            <td>CPU bound. Use `multiprocessing` instead.</td>
                        </tr>
                        <tr>
                            <td>Background Auto-Save</td>
                            <td>YES</td>
                            <td>Disk I/O releases GIL. UI stays responsive.</td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default PythonThreading;

